export type Granularity = "annual" | "quarterly" | "monthly" | "weekly";

export const GRANULARITIES: ReadonlyArray<{ key: Granularity; label: string }> =
  [
    { key: "annual", label: "Annual" },
    { key: "quarterly", label: "Quarterly" },
    { key: "monthly", label: "Monthly" },
    { key: "weekly", label: "Weekly" },
  ];

/**
 * Display label for a period row. Point-in-time rows (a balance sheet as at
 * the appointment date, ingested with an end date but no start date) are
 * stored with an annual "Year ended <date>" label; showing that as
 * "As at <date>" is honest — there is no year of trading behind the row,
 * just a position snapshot, so every flow field is empty. The stored
 * periodLabel is left untouched because it is the cross-document merge key.
 */
const FLOW_PROBE_FIELDS = [
  "revenue",
  "grossProfit",
  "operatingExpenses",
  "ebitda",
  "netProfitBeforeTax",
  "netProfitAfterTax",
] as const;

export function displayPeriodLabel(p: {
  periodLabel?: string;
  endDate?: string;
  [field: string]: unknown;
}): string {
  const label = p?.periodLabel ?? "";
  if (
    label.startsWith("Year ended") &&
    typeof p.endDate === "string" &&
    FLOW_PROBE_FIELDS.every((f) => p[f] == null)
  ) {
    const d = new Date(`${p.endDate}T00:00:00`);
    if (!isNaN(d.getTime())) {
      const month = d.toLocaleString("en-AU", { month: "short" });
      return `As at ${d.getDate()} ${month} ${d.getFullYear()}`;
    }
  }
  return label;
}

/** Coarsest granularity that has at least one entry; "annual" when all empty. */
export function pickDefaultGranularity(
  counts: Record<Granularity, number>,
): Granularity {
  for (const { key } of GRANULARITIES) {
    if ((counts[key] ?? 0) > 0) return key;
  }
  return "annual";
}
